import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatZAR, formatInt, formatApiError } from "@/lib/api";
import { PageHeader, KpiCard } from "@/components/Kpi";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { X, Search, Filter } from "lucide-react";

function formatKg(v) {
    if (v == null || isNaN(v) || v === 0) return "—";
    return `${Number(v).toLocaleString("en-ZA", { maximumFractionDigits: 2 })} kg`;
}

function todayMinusDays(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}

function OrderDrawer({ orderId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!orderId) return;
        setLoading(true);
        api.get(`/orders/${orderId}`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [orderId]);

    if (!orderId) return null;
    return (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose} data-testid="order-drawer">
            <div className="bg-[color:var(--bl-surface)] w-full max-w-2xl h-full overflow-y-auto shadow-2xl border-l border-[color:var(--bl-border)]" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-[color:var(--bl-border)] flex items-center justify-between sticky top-0 bg-[color:var(--bl-surface)] z-10">
                    <div>
                        <div className="font-serif-display text-2xl">Order #{orderId}</div>
                        <div className="text-xs text-[color:var(--bl-text-dim)] font-mono-num">{(data?.order_date || "").slice(0, 19).replace("T", " ")}</div>
                    </div>
                    <button data-testid="close-order-drawer" onClick={onClose} className="text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)]"><X className="w-5 h-5" /></button>
                </div>
                {loading && <div className="p-6 text-[color:var(--bl-text-dim)]">Loading…</div>}
                {data && (
                    <div className="p-6">
                        <div className="grid grid-cols-3 gap-2 mb-5">
                            <KpiCard label="Revenue" value={formatZAR(data.totals.revenue)} testid="drawer-revenue" />
                            <KpiCard label="Fees" value={formatZAR(data.totals.fees)} accent="orange" testid="drawer-fees" />
                            <KpiCard label="Net" value={formatZAR(data.totals.net)} accent="green" sub={data.margin_pct != null ? `${data.margin_pct.toFixed(1)}% margin` : ""} testid="drawer-net" />
                        </div>
                        <div className="text-xs text-[color:var(--bl-text-dim)] mb-2">
                            {data.totals.units} units · {formatKg(data.totals.kg)} · COGS {formatZAR(data.totals.cogs)}
                        </div>
                        <div className="font-serif-display text-lg mb-2">Line items</div>
                        <table className="w-full text-sm" data-testid="order-items-table">
                            <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)]">
                                <tr>
                                    <th className="text-left py-2">Item</th>
                                    <th className="text-right py-2">Qty</th>
                                    <th className="text-right py-2">Price</th>
                                    <th className="text-right py-2">Fees</th>
                                    <th className="text-right py-2">COGS</th>
                                    <th className="text-right py-2">Net</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((it) => (
                                    <tr key={it.order_item_id} className="border-b border-[color:var(--bl-border)]">
                                        <td className="py-2">
                                            <Link to={`/products/${encodeURIComponent(it.sku)}`} className="flex items-center gap-2 hover:opacity-80">
                                                {it.image_url && <img src={it.image_url} alt="" className="w-9 h-9 object-cover rounded-sm border border-[color:var(--bl-border)] bg-white" />}
                                                <div>
                                                    <div className="line-clamp-1">{it.display_name}</div>
                                                    <div className="font-mono-num text-[10px] text-[color:var(--bl-text-dim)]">{it.sku} · {it.sale_status}</div>
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="py-2 text-right font-mono-num">{it.quantity}</td>
                                        <td className="py-2 text-right font-mono-num">{formatZAR(it.selling_price)}</td>
                                        <td className="py-2 text-right font-mono-num text-[color:var(--bl-takealot-orange)]">{formatZAR(it.fees)}</td>
                                        <td className="py-2 text-right font-mono-num text-[color:var(--bl-text-dim)]">{formatZAR(it.cogs)}</td>
                                        <td className={`py-2 text-right font-mono-num ${it.net >= 0 ? "text-[color:var(--bl-success)]" : "text-[color:var(--bl-danger)]"}`}>{formatZAR(it.net)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="mt-4 text-xs text-[color:var(--bl-text-dim)]">
                            Region: <span className="text-[color:var(--bl-text)]">{data.items[0]?.sales_region}</span> · Source: <span className="text-[color:var(--bl-text)]">{data.items[0]?.stock_source_region}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function Sales() {
    const [filters, setFilters] = useState({
        start: todayMinusDays(30),
        end: new Date().toISOString().slice(0, 10),
        region: "",
        status: "",
        sku: "",
        search: "",
    });
    const [data, setData] = useState({ items: [], count: 0, totals: {}, statuses: [] });
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openOrder, setOpenOrder] = useState(null);

    const fetchSales = useCallback(() => {
        setLoading(true);
        const q = new URLSearchParams();
        if (filters.start) q.set("start", filters.start);
        if (filters.end) q.set("end", filters.end);
        if (filters.region) q.set("region", filters.region);
        if (filters.status) q.set("status", filters.status);
        if (filters.sku) q.set("sku", filters.sku);
        if (filters.search) q.set("search", filters.search);
        api.get(`/sales?${q.toString()}`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [filters]);

    useEffect(() => { fetchSales(); }, [fetchSales]);
    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data.items || [])).catch(() => {});
    }, []);

    const presets = [
        { label: "7d", start: todayMinusDays(7) },
        { label: "30d", start: todayMinusDays(30) },
        { label: "90d", start: todayMinusDays(90) },
        { label: "365d", start: todayMinusDays(365) },
        { label: "All", start: "2020-01-01" },
    ];

    const t = data.totals || {};

    const clearFilters = () => setFilters({ start: "", end: "", region: "", status: "", sku: "", search: "" });

    return (
        <div>
            <PageHeader title="Sales ledger" subtitle={`${data.count} sale rows matching your filter`}>
                <button data-testid="clear-filters-btn" onClick={clearFilters} className="text-xs text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-takealot-orange)] inline-flex items-center gap-1">
                    <X className="w-3 h-3" /> Clear
                </button>
            </PageHeader>

            <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3 mb-4">
                <KpiCard label="Revenue" value={formatZAR(t.revenue)} testid="sales-total-revenue" />
                <KpiCard label="Units" value={formatInt(t.units)} accent="orange" sub={`${formatInt(t.orders)} orders`} testid="sales-total-units" />
                <KpiCard label="Kg" value={formatKg(t.kg)} accent="coffee" testid="sales-total-kg" />
                <KpiCard label="Fees" value={formatZAR(t.fees)} accent="orange" testid="sales-total-fees" />
                <KpiCard label="COGS" value={formatZAR(t.cogs)} testid="sales-total-cogs" />
                <KpiCard label="Net" value={formatZAR(t.net)} accent="green" testid="sales-total-net" />
            </div>

            <div className="bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm shadow-sm p-4 mb-4">
                <div className="flex items-center gap-2 mb-3 text-[color:var(--bl-text-dim)] text-xs uppercase tracking-[0.18em]">
                    <Filter className="w-3.5 h-3.5" /> Filters
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">From</label>
                        <input data-testid="filter-start-date" type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">To</label>
                        <input data-testid="filter-end-date" type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Product</label>
                        <select data-testid="filter-sku" value={filters.sku} onChange={(e) => setFilters({ ...filters, sku: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]">
                            <option value="">All products</option>
                            {products.map((p) => (
                                <option key={p.sku} value={p.sku}>{(p.display_name || p.title || p.sku).slice(0, 40)}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Region</label>
                        <select data-testid="filter-region" value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]">
                            <option value="">All</option>
                            <option value="JHB">JHB</option>
                            <option value="CPT">CPT</option>
                            <option value="DBN">DBN</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Status</label>
                        <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]">
                            <option value="">All</option>
                            {data.statuses?.map((st) => <option key={st} value={st}>{st}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Search</label>
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--bl-text-dim)]" />
                            <input data-testid="filter-search" placeholder="Order # or SKU" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] pl-7 pr-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                    <span className="text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)]">Quick:</span>
                    {presets.map((p) => (
                        <button key={p.label} data-testid={`preset-${p.label}`} onClick={() => setFilters({ ...filters, start: p.start, end: new Date().toISOString().slice(0, 10) })}
                            className="text-xs px-2 py-0.5 border border-[color:var(--bl-border)] rounded-sm hover:bg-[color:var(--bl-surface-2)]">
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm overflow-x-auto shadow-sm">
                <table className="w-full text-sm min-w-[1000px]">
                    <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)] bg-[color:var(--bl-bg-2)]/40">
                        <tr>
                            <th className="text-left px-4 py-2">Date</th>
                            <th className="text-left px-3 py-2">Order</th>
                            <th className="text-left px-3 py-2">Product</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-right px-3 py-2">Qty</th>
                            <th className="text-right px-3 py-2">Kg</th>
                            <th className="text-right px-3 py-2">Price</th>
                            <th className="text-right px-3 py-2">Fees</th>
                            <th className="text-right px-3 py-2">Net</th>
                            <th className="text-left px-3 py-2">Region</th>
                        </tr>
                    </thead>
                    <tbody data-testid="sales-table">
                        {loading && (<tr><td colSpan={10} className="text-center text-[color:var(--bl-text-dim)] py-6">Loading…</td></tr>)}
                        {!loading && data.items.length === 0 && (<tr><td colSpan={10} className="text-center text-[color:var(--bl-text-dim)] py-6">No sales match these filters.</td></tr>)}
                        {data.items.map((s) => {
                            const fees = (s.total_fees || 0) + (s.stock_transfer_fee || 0);
                            const net = (s.selling_price || 0) * (s.quantity || 0) - fees - (s.cogs || 0);
                            return (
                                <tr key={s.order_item_id} data-testid={`sale-row-${s.order_item_id}`}
                                    onClick={() => setOpenOrder(s.order_id)}
                                    className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/50 cursor-pointer">
                                    <td className="px-4 py-2 font-mono-num text-xs">{(s.order_date || "").slice(0, 10)}</td>
                                    <td className="px-3 py-2 font-mono-num text-xs text-[color:var(--bl-takealot-blue)] hover:underline">#{s.order_id}</td>
                                    <td className="px-3 py-2">
                                        <div className="text-[color:var(--bl-text)] line-clamp-1">{s.display_name || s.sku}</div>
                                        <div className="font-mono-num text-[10px] text-[color:var(--bl-text-dim)]">{s.sku}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{s.sale_status}</td>
                                    <td className="px-3 py-2 text-right font-mono-num">{formatInt(s.quantity)}</td>
                                    <td className="px-3 py-2 text-right font-mono-num text-[color:var(--bl-accent-coffee)]">{formatKg(s.kg)}</td>
                                    <td className="px-3 py-2 text-right font-mono-num">{formatZAR(s.selling_price)}</td>
                                    <td className="px-3 py-2 text-right font-mono-num text-[color:var(--bl-takealot-orange)]">{formatZAR(fees)}</td>
                                    <td className={`px-3 py-2 text-right font-mono-num ${net >= 0 ? "text-[color:var(--bl-success)]" : "text-[color:var(--bl-danger)]"}`}>{formatZAR(net)}</td>
                                    <td className="px-3 py-2 text-xs">{s.sales_region}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                    {!loading && data.items.length > 0 && (
                        <tfoot className="bg-[color:var(--bl-bg-2)]/60 border-t-2 border-[color:var(--bl-border-strong)] font-mono-num">
                            <tr data-testid="sales-totals-row">
                                <td colSpan={4} className="px-4 py-2 text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)]">
                                    Shipped totals · {data.totals.orders || 0} orders · COGS {formatZAR(t.cogs)}
                                </td>
                                <td className="px-3 py-2 text-right">{formatInt(t.units)}</td>
                                <td className="px-3 py-2 text-right text-[color:var(--bl-accent-coffee)]">{formatKg(t.kg)}</td>
                                <td className="px-3 py-2 text-right">{formatZAR(t.revenue)}</td>
                                <td className="px-3 py-2 text-right text-[color:var(--bl-takealot-orange)]">{formatZAR(t.fees)}</td>
                                <td className={`px-3 py-2 text-right ${t.net >= 0 ? "text-[color:var(--bl-success)]" : "text-[color:var(--bl-danger)]"}`}>{formatZAR(t.net)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
            {data.excluded && data.excluded.rows > 0 && (
                <div data-testid="sales-excluded-note" className="mt-2 px-3 py-2 border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm text-xs text-[color:var(--bl-text-dim)]">
                    <span className="font-medium text-[color:var(--bl-text)] uppercase tracking-wider text-[10px] mr-2">Excluded from totals:</span>
                    {Object.entries(data.excluded.by_status).map(([st, n], i) => (
                        <span key={st} className="mr-3">
                            {st} ({n})
                        </span>
                    ))}
                    <span className="ml-1">— total {data.excluded.rows} rows, {data.excluded.units} units, <span className="font-mono-num">{formatZAR(data.excluded.revenue)}</span></span>
                </div>
            )}
            {data.items.length === 5000 && (
                <div className="text-xs text-[color:var(--bl-text-dim)] mt-2">Showing first 5,000. Narrow your filter for the full set.</div>
            )}

            <OrderDrawer orderId={openOrder} onClose={() => setOpenOrder(null)} />
        </div>
    );
}
